import type { CatalogSearchItem } from "@/types/common/filters";
import type { RoleType } from "@/types/entities/roles";
import type { TagType } from "@/types/entities/tags";
import type { UserType } from "@/types/entities/users";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Icon,
} from "@elaniintc/goes-design-system";

export const UserCard = ({
  user,
  onUpdateSearch,
}: {
  user: UserType;
  onUpdateSearch: (type: "remove" | "add", value: UserType) => void;
}) => {
  return (
    <div className="min-w-83 min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg">
      <div className="grid grid-cols-8">
        <div className="col-span-1 flex justify-start items-center">
          <Avatar size="xs">
            <AvatarFallback color="action">
              {user.name ? `${user.name.charAt(0)}` : ""}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="col-span-6 flex flex-col p-0 m-0 justify-start items-start">
          <span className="font-semibold span-0 m-0 text-base leading-3xl truncate max-w-full">{`${user.name} ${user.last_name}`}</span>
          <span className="span-0 m-0 text-sm font-normal leading-tight truncate max-w-full">
            {user.email}
          </span>
        </div>
        <div className="col-span-1 flex justify-end items-center">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove user"
            className="cursor-pointer p-0 m-0"
            onClick={() => onUpdateSearch("remove", user)}
          >
            <Icon
              type="far"
              name="xmark"
              className="font-normal text-body text-base leading-3xl"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const RoleCard = ({
  role,
  onUpdateSearch,
}: {
  role: RoleType;
  onUpdateSearch: (type: "remove" | "add", value: RoleType) => void;
}) => {
  return (
    <div className="min-w-83 min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg flex justify-between">
      <div className="w-4/5">
        <p className="font-semibold text-base leading-3xl truncate">{`${role.name}`}</p>
        <p className="text-sm font-normal leading-tight truncate">
          {role.description || "-"}
        </p>
      </div>
      <div className="flex justify-end items-center shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remover rol"
          className="cursor-pointer p-0 m-0"
          onClick={() => onUpdateSearch("remove", role)}
        >
          <Icon
            type="far"
            name="xmark"
            className="font-normal text-body text-base leading-3xl"
          />
        </Button>
      </div>
    </div>
  );
};

export const TagCard = ({
  tag,
  onUpdateSearch,
}: {
  tag: TagType;
  onUpdateSearch: (type: "remove" | "add", value: TagType) => void;
}) => {
  const imageUrl = tag.files && tag.files.length > 0 ? tag.files[0].url : null;

  return (
    <div className="min-w-83 min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg flex justify-between">
      <div className=" w-full flex gap-2 items-center truncate">
        <Avatar size="xs" shape="square">
          <AvatarImage src={imageUrl || ""} alt={tag.name} />
          <AvatarFallback color="neutral" shape="square" type="icon">
            <Icon type="fas" name="tag" />
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
          <span className="text-body font-semibold whitespace-nowrap">
            {tag.name}
          </span>
          <p className="text-body-quiet font-normal truncate text-xs">
            {tag.description || "-"}
          </p>
        </div>
      </div>
      <div className="flex justify-end items-center shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remover rol"
          className="cursor-pointer p-0 m-0"
          onClick={() => onUpdateSearch("remove", tag)}
        >
          <Icon
            type="far"
            name="xmark"
            className="font-normal text-body text-base leading-3xl"
          />
        </Button>
      </div>
    </div>
  );
};

export const CatalogCard = ({
  item,
  onUpdateSearch,
}: {
  item: CatalogSearchItem;
  onUpdateSearch: (type: "remove" | "add", value: CatalogSearchItem) => void;
}) => {
  return (
    <div className="min-w-83 min-h-12 border border-neutral-quiet text-body bg-transparent py-2 px-3 rounded-lg flex justify-between">
      <div className="w-4/5">
        <p className="font-semibold text-base leading-3xl truncate">
          {item.name || item.nombre || "-"}
        </p>
        <p className="text-sm font-normal leading-tight truncate">
          {item.description || "-"}
        </p>
      </div>
      <div className="flex justify-end items-center shrink-0">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remover elemento"
          className="cursor-pointer p-0 m-0"
          onClick={() => onUpdateSearch("remove", item)}
        >
          <Icon
            type="far"
            name="xmark"
            className="font-normal text-body text-base leading-3xl"
          />
        </Button>
      </div>
    </div>
  );
};
